import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getCurrentUser, logout, onAuthStateChange, getEncryptionKey } from "@/lib/auth";
import { isUserAdmin, getUserProfile } from "@/lib/admin";
import { Button } from "@/components/ui/button";
import {
  HelpCircle,
  Settings as SettingsIcon,
  User as UserIcon
} from "lucide-react";

interface AppHeaderProps {
  showAuth?: boolean;
}

const AppHeader = ({ showAuth = true }: AppHeaderProps) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(getCurrentUser());
  const [isAdmin, setIsAdmin] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const adminStatus = await isUserAdmin(currentUser.id);
        setIsAdmin(adminStatus);

        // Fetch user profile to get name
        const profile = await getUserProfile(currentUser.id);
        if (profile?.name && !profile.name.startsWith('[Encrypted Name')) {
          setUserName(profile.name);
        } else {
          // Name is encrypted or unavailable, fallback to email
          setUserName(null);
        }
      } else {
        setIsAdmin(false);
        setUserName(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Monitor encryption key changes and update user name when encryption becomes available
  useEffect(() => {
    if (!user) return;

    const checkInterval = setInterval(async () => {
      const encryptionKey = getEncryptionKey();

      // If we don't have a user name and encryption key is now available, try to fetch profile again
      if (!userName && encryptionKey) {
        console.log('🔄 [AppHeader] Encryption key detected, re-fetching profile...');
        const profile = await getUserProfile(user.id);
        if (profile?.name && !profile.name.startsWith('[Encrypted Name')) {
          console.log('✅ [AppHeader] User name decrypted:', profile.name);
          setUserName(profile.name);
          clearInterval(checkInterval); // Stop checking once we have the name
        }
      }

      // If we already have a name, no need to keep checking
      if (userName) {
        clearInterval(checkInterval);
      }
    }, 500); // Check every 500ms

    // Cleanup interval after 10 seconds max
    const timeout = setTimeout(() => {
      clearInterval(checkInterval);
    }, 10000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, [user, userName]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-background focus:text-foreground">
        Skip to content
      </a>
      <header className="flex items-center justify-between whitespace-nowrap border-b border-border px-4 sm:px-6 lg:px-10 py-4 bg-card">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-3 text-primary hover:opacity-80 transition-opacity">
        <img src="/assets/logo_new_clean.png" alt="Logo" className="h-12 w-12 object-contain" />
        <span className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>The Love Contract</span>
      </Link>
      <nav aria-label="Main navigation" className="flex items-center gap-4">
        {showAuth && user && (
          <>
            <Link
              to="/profile"
              className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <UserIcon className="h-4 w-4" />
              <span>{userName || 'Profile'}</span>
            </Link>
            <Link to="/profile" className="sm:hidden text-muted-foreground hover:text-primary transition-colors">
              <UserIcon className="h-6 w-6" />
            </Link>
            <Button onClick={handleLogout} variant="ghost" className="h-10 px-4">
              Logout
            </Button>
          </>
        )}
        <button className="text-muted-foreground hover:text-primary transition-colors">
          <Link to="/help" aria-label="Help Center">
            <HelpCircle className="h-6 w-6" />
          </Link>
        </button>
        {user && isAdmin && (
          <Link to="/settings" className="text-muted-foreground hover:text-primary transition-colors" title="Admin Settings">
            <SettingsIcon className="h-6 w-6" />
          </Link>
        )}
      </nav>
    </header>
    </>
  );
};

export default AppHeader;
